import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { RefreshCwIcon } from 'lucide-react';
import { FieldErrors, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getValidationParamFromYupObjectSchema } from '@/util/validation/helpers';
import { emailSenderSchema } from '@/util/validation/rules/email-rules';

/**
 * Form data structure for email sender input
 */
type EmailSenderFormData = yup.InferType<typeof emailSenderSchema>;

/**
 * Props for the EmailSenderInput component
 */
interface AgentInfoProps {
  emailName?: string;
  emailAddress?: string;
  /** Callback when verify button is clicked */
  onVerify?: (data: EmailSenderFormData) => void;
  /** Whether the verify button should be disabled */
  isVerifyDisabled?: boolean;
  /** Whether the form is in a loading state */
  isLoading?: boolean;
  /** Whether the user has write permissions */
  canWrite?: boolean;
}

/**
 * EmailSenderInput - A form component for configuring email sender information using React Hook Form
 *
 * Features:
 * - Sender email input with email validation
 * - Sender name input with validation
 * - Character count display (0/48 format)
 * - Helper text for each field
 * - Verify button with loading state
 * - Responsive grid layout
 * - Form validation with error messages
 *
 * @param props - Component props
 * @returns {JSX.Element} The email sender input component
 */
export const AgentInfo = ({
  emailName,
  emailAddress,
  onVerify,
  isLoading = false,
  canWrite = true,
}: AgentInfoProps) => {
  const { t } = useTranslation();
  const form = useForm<EmailSenderFormData>({
    resolver: yupResolver<EmailSenderFormData, unknown, EmailSenderFormData>(emailSenderSchema),
    defaultValues: { from_email: emailAddress || '', from_name: emailName || '' },
    mode: 'onSubmit',
  });

  const {
    formState: { isSubmitting, isDirty },
  } = form;

  // Check if form is already filled on load
  const isFormFilledOnLoad = !!emailAddress && !!emailName;

  // Auto-focus the sender email field if the form is empty on load
  useEffect(() => {
    if (!emailAddress && !emailName) {
      form.setFocus('from_email');
    }
    // reset form if email address or name is changed
    form.reset(
      { from_email: emailAddress || '', from_name: emailName || '' },
      { keepDirty: false }
    );
  }, [emailAddress, emailName, form]);

  const onInvalidSubmit = useCallback(
    (errors: FieldErrors<EmailSenderFormData>) => {
      // When both fields are empty, clear errors and focus the first field
      if (errors.from_email?.type === 'required' && errors.from_name?.type === 'required') {
        form.clearErrors();
        errors.from_email.ref?.focus();
        return;
      }
    },
    [form]
  );

  const onValidSubmit = useCallback(
    (data: EmailSenderFormData) => {
      // If form is filled on load but not dirty, focus the name input
      if (isFormFilledOnLoad && !isDirty) {
        form.setFocus('from_name');
        return;
      }

      // If the form is dirty and onVerify is provided, call onVerify
      if (isDirty && onVerify) {
        onVerify(data);
      }
    },
    [isDirty, onVerify, isFormFilledOnLoad, form]
  );

  return (
    <Card className="w-full shadow-none">
      <CardContent className="px-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onValidSubmit, onInvalidSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] md:gap-12">
              {/* Sender Email Field */}
              <FormField
                disabled={!!emailAddress || !canWrite}
                control={form.control}
                name="from_email"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-sm leading-5 font-medium">
                        {t('email.sender_email')}
                      </FormLabel>
                      <span className="text-sm leading-5 text-foreground">
                        {field.value?.length || 0}/
                        {getValidationParamFromYupObjectSchema(
                          emailSenderSchema,
                          field.name,
                          'max'
                        )}
                      </span>
                    </div>
                    <FormControl>
                      {emailAddress ? (
                        <InfoTooltip content={t('email.sender_email_locked_tooltip')}>
                          <div>
                            <Input {...field} className="w-full" />
                          </div>
                        </InfoTooltip>
                      ) : (
                        <Input {...field} className="w-full" />
                      )}
                    </FormControl>
                    <FormDescription>{t('email.sender_email_placeholder')}</FormDescription>
                    <div className="min-h-5">
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Sender Name Field */}
              <FormField
                disabled={!canWrite}
                control={form.control}
                name="from_name"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-sm leading-5 font-medium">
                        {t('email.sender_name')}
                      </FormLabel>
                      <span className="text-sm leading-5 text-foreground">
                        {field.value?.length || 0}/
                        {getValidationParamFromYupObjectSchema(
                          emailSenderSchema,
                          field.name,
                          'max'
                        )}
                      </span>
                    </div>
                    <FormControl>
                      <Input {...field} type="text" className="w-full" />
                    </FormControl>
                    <FormDescription>{t('email.sender_name_placeholder')}</FormDescription>
                    <div className="min-h-5">
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Verify/Update Button */}
              <div className="flex flex-col gap-4 py-7">
                <Button
                  type="submit"
                  disabled={!canWrite}
                  isLoading={isLoading || isSubmitting}
                  variant={isFormFilledOnLoad ? 'outline-neutral' : 'primary'}
                  className="flex h-10 min-w-[142px] items-center justify-center gap-2"
                >
                  <RefreshCwIcon />
                  {isFormFilledOnLoad ? t('email.update_name') : t('email.verify')}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
